import React, { useEffect, useState } from 'react';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FastForwardOutlined,
  LinkOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  RocketOutlined,
  RollbackOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Card,
  Flex,
  Input,
  InputNumber,
  Modal,
  Progress,
  Space,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { styled } from 'styled-components';

import {
  CanaryMetricConfig,
  CanaryPhase,
  CanaryRollout,
  CanaryStatusProps,
  MetricCheckEntry,
} from './types';
import {
  CanaryMonitorStatusPanel,
  getAggregateMonitorStatus,
  getDatadogMonitorNames,
  getDatadogMonitorStatusColor,
  getLatestMonitorStatuses,
  isDatadogMonitorName,
  normalizeDatadogMonitorStatus,
  withoutDatadogMonitors,
} from '@/features/variable/canary-monitor-status';

const { Text, Title } = Typography;

// ---------------------------------------------------------------------------
// Decision value → human-readable label
// ---------------------------------------------------------------------------

const DECISION_LABEL: Record<string, string> = {
  user_promoted: 'Promoted',
  user_rolled_back: 'Rolled Back',
  user_overridden: 'Overridden',
  advance: 'Advanced',
  user_held: 'Held',
  promoted: 'Promoted',
  rolled_back: 'Rolled Back',
  cancelled: 'Cancelled',
};

// ---------------------------------------------------------------------------
// Styled helpers
// ---------------------------------------------------------------------------

const ChartContainer = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;

const ChartArea = styled.div`
  position: relative;
  height: 60px;
  display: flex;
  align-items: flex-end;
  gap: 1px;
`;

const ChartBar = styled.div<{ $color: string }>`
  flex: 1;
  min-width: 3px;
  max-width: 12px;
  border-radius: 2px 2px 0 0;
  background-color: ${({ $color }) => $color};
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.7;
  }
`;

const ThresholdLine = styled.div<{ $pct: number; $color: string }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${({ $pct }) => $pct}%;
  height: 1px;
  background: ${({ $color }) => $color};
  border-top: 1px dashed ${({ $color }) => $color};
  pointer-events: none;
  z-index: 1;
  &::after {
    content: attr(data-label);
    position: absolute;
    right: 0;
    top: -14px;
    font-size: 9px;
    color: ${({ $color }) => $color};
    white-space: nowrap;
  }
`;

const ChartLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.45);
`;

const MetricLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  font-size: 11px;
`;

const LegendDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  margin-right: 4px;
`;

const BreachedMetricTag = styled(Tag)`
  margin-bottom: 2px;
`;

const DiffLink = styled.a`
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const MetricSnapshotTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 4px;

  th {
    text-align: left;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 500;
    padding: 2px 8px 2px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  td {
    padding: 3px 8px 3px 0;
    border-bottom: 1px solid #f8f8f8;
  }

  td.breached {
    color: #ff4d4f;
    font-weight: 600;
  }

  td.improved {
    color: #52c41a;
  }
`;

const LastCheckedText = styled(Text)`
  font-size: 11px;
  color: rgba(0, 0, 0, 0.35);
`;

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

const formatElapsedTime = (startTime: string): string => {
  const diff = Date.now() - new Date(startTime).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const getPhaseStatusColor = (
  status: CanaryPhase['status'],
): 'processing' | 'warning' | 'error' | 'success' | 'default' => {
  switch (status) {
    case 'in_progress':
      return 'processing';
    case 'awaiting_decision':
      return 'warning';
    case 'threshold_breached':
      return 'error';
    case 'completed':
      return 'success';
    case 'rolled_back':
      return 'error';
    case 'skipped':
      return 'default';
    case 'yet_to_start':
    default:
      return 'default';
  }
};

const getPhaseStatusLabel = (status: CanaryPhase['status']): string => {
  switch (status) {
    case 'in_progress':
      return 'In Progress';
    case 'awaiting_decision':
      return 'Awaiting Decision';
    case 'threshold_breached':
      return 'Threshold Breached';
    case 'completed':
      return 'Completed';
    case 'rolled_back':
      return 'Rolled Back';
    case 'skipped':
      return 'Skipped';
    case 'yet_to_start':
    default:
      return 'Yet to Start';
  }
};

const calculateProgress = (canary: CanaryRollout): number => {
  if (!canary.phases.length) return 0;

  // Fully promoted/completed → 100%
  if (
    canary.status === 'completed' &&
    canary.finalDecision !== 'rolled_back' &&
    canary.finalDecision !== 'cancelled'
  )
    return 100;

  // Active phase: show its target rollout percentage
  const activePhase = canary.phases.find(
    (p) =>
      p.status === 'in_progress' ||
      p.status === 'awaiting_decision' ||
      p.status === 'threshold_breached',
  );
  if (activePhase) return activePhase.targetPercentage;

  // No active phase — show the last completed phase's percentage
  const lastCompleted = [...canary.phases]
    .reverse()
    .find((p) => p.status === 'completed');
  if (lastCompleted) return lastCompleted.targetPercentage;

  return 0;
};

const getLastBreachedEntry = (
  log?: MetricCheckEntry[],
): MetricCheckEntry | undefined => {
  if (!log || log.length === 0) return undefined;
  for (let i = log.length - 1; i >= 0; i--) {
    if (log[i].breached) return log[i];
  }
  return undefined;
};

// Returns the latest metric values from the check log, scanning backwards to
// skip error/empty entries. Used as a fallback when metricValuesAtEnd is nil.
const lastCheckLogSnapshot = (
  log?: MetricCheckEntry[],
): Record<string, number> | null => {
  if (!log || log.length === 0) return null;
  for (let i = log.length - 1; i >= 0; i--) {
    if (log[i].metrics && Object.keys(log[i].metrics!).length > 0) {
      return log[i].metrics!;
    }
  }
  return null;
};

// Returns true when the phase transitioned to awaiting_decision because of
// consecutive Datadog read failures (not because the observation period expired
// normally). Detected by checking whether the last 2 check log entries both
// have a non-empty error field.
const isMetricFailureCause = (phase?: CanaryPhase): boolean => {
  const log = phase?.metricCheckLog;
  if (!log || log.length < 2) return false;
  return !!log[log.length - 1].error && !!log[log.length - 2].error;
};

const formatMetricValue = (value: number): string => {
  if (Number.isInteger(value)) return value.toString();
  return value.toPrecision(4);
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const METRIC_COLORS = [
  '#1890ff',
  '#52c41a',
  '#722ed1',
  '#fa8c16',
  '#eb2f96',
  '#13c2c2',
];

const SingleMetricChart: React.FC<{
  metricName: string;
  entries: MetricCheckEntry[];
  metricConfig?: CanaryMetricConfig;
  failedCount: number;
}> = ({ metricName, entries, metricConfig, failedCount }) => {
  const entriesWithData = entries.filter(
    (e) => e.metrics && e.metrics[metricName] != null,
  );
  if (entriesWithData.length === 0) return null;

  let maxVal = 0;
  entriesWithData.forEach((e) => {
    const v = e.metrics[metricName];
    if (v > maxVal) maxVal = v;
  });

  // Extend maxVal to accommodate threshold lines so they don't clip
  if (metricConfig?.max != null && metricConfig.max > maxVal)
    maxVal = metricConfig.max * 1.1;
  if (metricConfig?.min != null && metricConfig.min > maxVal)
    maxVal = metricConfig.min * 1.1;
  if (maxVal === 0) maxVal = 1;

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const thresholdLines: Array<{ pct: number; label: string; color: string }> =
    [];
  if (metricConfig?.max != null) {
    thresholdLines.push({
      pct: Math.min(99, Math.round((metricConfig.max / maxVal) * 100)),
      label: `max: ${metricConfig.max}`,
      color: '#ff7875',
    });
  }
  if (metricConfig?.min != null) {
    thresholdLines.push({
      pct: Math.min(99, Math.round((metricConfig.min / maxVal) * 100)),
      label: `min: ${metricConfig.min}`,
      color: '#52c41a',
    });
  }

  return (
    <ChartContainer>
      <Text
        type="secondary"
        style={{ fontSize: 11, marginBottom: 4, display: 'block' }}
      >
        <strong>{metricConfig?.displayName ?? metricName}</strong> — values over
        time ({entriesWithData.length} checks)
        {failedCount > 0 && (
          <span style={{ color: '#faad14', marginLeft: 8 }}>
            ⚠ {failedCount} check{failedCount > 1 ? 's' : ''} failed
          </span>
        )}
      </Text>
      <ChartArea>
        {thresholdLines.map((t, i) => (
          <ThresholdLine
            key={`threshold-${i}`}
            $pct={t.pct}
            $color={t.color}
            data-label={t.label}
          />
        ))}
        {entriesWithData.map((entry, idx) => {
          const val = entry.metrics[metricName];
          const heightPct = Math.max(5, Math.round((val / maxVal) * 100));
          const barColor = entry.breached ? '#ff4d4f' : METRIC_COLORS[0];

          return (
            <Tooltip
              key={idx}
              title={
                <div>
                  <div>{formatTime(entry.checkedAt)}</div>
                  <div>
                    {metricConfig?.displayName ?? metricName}:{' '}
                    {formatMetricValue(val)}
                  </div>
                  {entry.breached && (
                    <div style={{ color: '#ff4d4f' }}>BREACHED</div>
                  )}
                </div>
              }
            >
              <ChartBar $color={barColor} style={{ height: `${heightPct}%` }} />
            </Tooltip>
          );
        })}
      </ChartArea>
      <ChartLabel>
        <span>
          {entriesWithData.length > 0 &&
            formatTime(entriesWithData[0].checkedAt)}
        </span>
        <span>
          {entriesWithData.length > 1 &&
            formatTime(entriesWithData[entriesWithData.length - 1].checkedAt)}
        </span>
      </ChartLabel>
      <MetricLegend>
        <span>
          <LegendDot $color={METRIC_COLORS[0]} />
          {metricConfig?.displayName ?? metricName}
        </span>
        <span>
          <LegendDot $color="#ff4d4f" />
          Breached
        </span>
        {thresholdLines.length > 0 && (
          <>
            {metricConfig?.max != null && (
              <span>
                <LegendDot $color="#ff7875" />
                Max threshold
              </span>
            )}
            {metricConfig?.min != null && (
              <span>
                <LegendDot $color="#52c41a" />
                Min threshold
              </span>
            )}
          </>
        )}
      </MetricLegend>
    </ChartContainer>
  );
};

const MetricChart: React.FC<{
  entries: MetricCheckEntry[];
  metricsConfig?: CanaryMetricConfig[];
}> = ({ entries, metricsConfig }) => {
  const entriesWithData = entries.filter(
    (e) => e.metrics && Object.keys(e.metrics).length > 0,
  );
  const failedCount = entries.filter((e) => !!e.error).length;
  if (entriesWithData.length === 0) return null;

  const allMetricNames = Array.from(
    new Set(entriesWithData.flatMap((e) => Object.keys(e.metrics))),
  ).filter((name) => !isDatadogMonitorName(name, metricsConfig));

  if (allMetricNames.length === 0) return null;

  return (
    <>
      {allMetricNames.map((name) => {
        const config = metricsConfig?.find(
          (m) => m.displayName === name || m.query?.includes(name),
        );
        return (
          <SingleMetricChart
            key={name}
            metricName={name}
            entries={entries}
            metricConfig={config}
            failedCount={failedCount}
          />
        );
      })}
    </>
  );
};

const LatestMetricValues: React.FC<{
  log: MetricCheckEntry[];
  metricsConfig?: CanaryMetricConfig[];
}> = ({ log, metricsConfig }) => {
  const last = log[log.length - 1];

  const hasQueryMetrics = (entry?: MetricCheckEntry) =>
    Object.keys(withoutDatadogMonitors(entry?.metrics, metricsConfig) ?? {})
      .length > 0;

  if (last?.error) {
    return (
      <Text type="warning" style={{ fontSize: 12 }}>
        Latest ({new Date(last.checkedAt).toLocaleTimeString()}): Metric read
        failed — {last.error}
      </Text>
    );
  }

  const lastWithData = [...log].reverse().find(hasQueryMetrics);

  if (!lastWithData) {
    return null;
  }

  const queryMetrics = withoutDatadogMonitors(
    lastWithData.metrics,
    metricsConfig,
  )!;
  const metricKeys = Object.keys(queryMetrics);
  const time = new Date(lastWithData.checkedAt).toLocaleTimeString();

  return (
    <Text type="secondary" style={{ fontSize: 12 }}>
      Latest ({time}):{' '}
      {metricKeys.map((key, idx) => (
        <span
          key={key}
          style={{ color: lastWithData.breached ? '#ff4d4f' : undefined }}
        >
          <strong>{key}</strong>={formatMetricValue(queryMetrics[key])}
          {idx < metricKeys.length - 1 ? ', ' : ''}
        </span>
      ))}
    </Text>
  );
};

const MetricSnapshotComparison: React.FC<{
  phase: CanaryPhase;
  metricsConfig?: CanaryMetricConfig[];
}> = ({ phase, metricsConfig }) => {
  const start = phase.metricValuesAtStart;

  // Use metricValuesAtEnd when available (phase completed with a snapshot).
  // Fall back to the latest entry in the check log so that "current" metric
  // values and deltas are always visible even while the phase is in_progress
  // or when Datadog didn't produce a snapshot at expiry.
  const end =
    phase.metricValuesAtEnd ?? lastCheckLogSnapshot(phase.metricCheckLog);
  const endLabel = phase.metricValuesAtEnd ? 'At End' : 'Current';

  const queryStart = withoutDatadogMonitors(start, metricsConfig);
  const queryEnd = withoutDatadogMonitors(end, metricsConfig);

  if (!queryStart && !queryEnd) return null;

  const allKeys = Array.from(
    new Set([...Object.keys(queryStart ?? {}), ...Object.keys(queryEnd ?? {})]),
  );

  if (allKeys.length === 0) return null;

  return (
    <div>
      <Text
        type="secondary"
        style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
      >
        Metric snapshot (start vs {endLabel.toLowerCase()})
      </Text>
      <MetricSnapshotTable>
        <thead>
          <tr>
            <th>Metric</th>
            <th>At Start</th>
            <th>{endLabel}</th>
            <th>Δ</th>
            <th>Threshold</th>
          </tr>
        </thead>
        <tbody>
          {allKeys.map((key) => {
            const startVal = queryStart?.[key];
            const endVal = queryEnd?.[key];
            const config = metricsConfig?.find((m) => m.displayName === key);
            let delta = '';
            let deltaClass = '';
            if (startVal != null && endVal != null) {
              const d = endVal - startVal;
              delta = (d >= 0 ? '+' : '') + formatMetricValue(d);
              if (config?.max != null) {
                deltaClass =
                  endVal > config.max
                    ? 'breached'
                    : endVal > startVal
                      ? ''
                      : 'improved';
              }
            }
            const isBreached =
              config?.max != null && endVal != null && endVal > config.max;
            const isBelowMin =
              config?.min != null && endVal != null && endVal < config.min;
            const thresholdLabel =
              config?.max != null
                ? `max ${config.max}`
                : config?.min != null
                  ? `min ${config.min}`
                  : '—';

            return (
              <tr key={key}>
                <td>
                  <strong>{key}</strong>
                </td>
                <td>{startVal != null ? formatMetricValue(startVal) : '—'}</td>
                <td className={isBreached || isBelowMin ? 'breached' : ''}>
                  {endVal != null ? formatMetricValue(endVal) : '—'}
                </td>
                <td className={deltaClass}>{delta || '—'}</td>
                <td style={{ color: 'rgba(0,0,0,0.35)' }}>{thresholdLabel}</td>
              </tr>
            );
          })}
        </tbody>
      </MetricSnapshotTable>
    </div>
  );
};

const BreachDetails: React.FC<{
  phase: CanaryPhase;
  metricsConfig?: CanaryMetricConfig[];
}> = ({ phase, metricsConfig }) => {
  const breachedEntry = getLastBreachedEntry(phase.metricCheckLog);
  if (!breachedEntry) return null;

  const breachedNames = breachedEntry.breachedMetrics ?? [];
  if (breachedNames.length === 0) return null;

  return (
    <Flex vertical gap={4} style={{ marginTop: 4 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Breached metrics:
      </Text>
      <Flex gap={4} wrap="wrap">
        {breachedNames.map((name) => {
          const metricValue = breachedEntry.metrics?.[name];

          return (
            <BreachedMetricTag color="red" key={name}>
              {name}
              {metricValue != null &&
                (isDatadogMonitorName(name, metricsConfig)
                  ? ` — ${normalizeDatadogMonitorStatus(metricValue)}`
                  : ` = ${formatMetricValue(metricValue)}`)}
            </BreachedMetricTag>
          );
        })}
      </Flex>
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// Decision modal
// ---------------------------------------------------------------------------

interface DecisionModalState {
  type: 'advance' | 'promote' | 'rollback' | 'override';
  reason: string;
}

const DECISION_MODAL_CONFIG: Record<
  DecisionModalState['type'],
  {
    title: string;
    reasonRequired: boolean;
    okText: string;
    okDanger?: boolean;
    description?: string;
  }
> = {
  advance: {
    title: 'Advance to Next Phase',
    reasonRequired: false,
    okText: 'Advance',
    description:
      'Move to the next canary phase with increased traffic percentage.',
  },
  promote: {
    title: 'Promote (Go Live)',
    reasonRequired: false,
    okText: 'Promote',
    description: 'Apply the canary changes to 100% of traffic permanently.',
  },
  rollback: {
    title: 'Rollback Canary',
    reasonRequired: true,
    okText: 'Rollback',
    okDanger: true,
    description:
      'This will restore the pre-canary state. All users will revert to the original values.',
  },
  override: {
    title: 'Override Threshold Breach',
    reasonRequired: true,
    okText: 'Override & Advance',
    okDanger: true,
    description:
      'Acknowledge the metric breach and advance to the next phase anyway. This decision is permanently recorded in the audit trail.',
  },
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const CanaryStatusSection: React.FC<CanaryStatusProps> = ({
  canaryRollout,
  isLoading = false,
  error,
  onAdvance,
  onHold,
  onRollback,
  onPromote,
  onRefresh,
  onModalStateChange,
  showCanaryActiveWarning = false,
}) => {
  const [holdMins, setHoldMins] = useState<number | null>(30);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [decisionModal, setDecisionModal] = useState<DecisionModalState | null>(
    null,
  );

  // Notify parent when any modal opens or closes so the 60s refresh timer
  // can be paused while the user is actively typing in the modal.
  useEffect(() => {
    onModalStateChange?.(!!decisionModal || showHoldModal);
  }, [decisionModal, showHoldModal, onModalStateChange]);

  // The parent (RolloutTabV2) owns the 60s refresh timer and calls onRefresh,
  // which re-fetches canary status and rollout data together. Update the
  // lastRefreshed timestamp here whenever the parent pushes new data.
  useEffect(() => {
    if (canaryRollout) {
      setLastRefreshed(new Date());
    }
  }, [canaryRollout]);

  // Phases collapsed by default; only the active/awaiting phase starts expanded
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(() => {
    const active = new Set<number>();
    if (canaryRollout) {
      canaryRollout.phases.forEach((p) => {
        if (
          p.status === 'in_progress' ||
          p.status === 'awaiting_decision' ||
          p.status === 'threshold_breached'
        ) {
          active.add(p.phaseIndex);
        }
      });
    }
    return active;
  });

  const togglePhase = (phaseIndex: number) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseIndex)) next.delete(phaseIndex);
      else next.add(phaseIndex);
      return next;
    });
  };

  if (!canaryRollout && !isLoading && !error) return null;

  if (error) {
    return (
      <Alert
        type="error"
        message="Canary Status Error"
        description={error}
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!canaryRollout && isLoading) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <Text type="secondary">Loading canary status...</Text>
      </Card>
    );
  }

  const currentPhase = canaryRollout.phases[canaryRollout.currentPhaseIndex];
  const isActive = canaryRollout.status === 'active';
  const isBreached = currentPhase?.status === 'threshold_breached';
  const isActionable =
    currentPhase?.status === 'awaiting_decision' ||
    currentPhase?.status === 'threshold_breached';
  const isLastPhase =
    canaryRollout.currentPhaseIndex === canaryRollout.phases.length - 1;
  const progress = calculateProgress(canaryRollout);

  const handleDecisionConfirm = () => {
    if (!decisionModal) return;
    const { type, reason } = decisionModal;

    switch (type) {
      case 'advance':
        onAdvance?.(canaryRollout.id, reason || 'Advance from portal');
        break;
      case 'promote':
        onPromote?.(canaryRollout.id, reason || 'Promote from portal');
        break;
      case 'rollback':
        onRollback?.(canaryRollout.id, reason);
        break;
      case 'override':
        // Treat override as a regular advance — backend handles it identically.
        // The only difference is reason is mandatory (enforced by modal config).
        onAdvance?.(canaryRollout.id, reason);
        break;
    }
    setDecisionModal(null);
  };

  const openDecision = (type: DecisionModalState['type']) => {
    setDecisionModal({ type, reason: '' });
  };

  const modalConfig = decisionModal
    ? DECISION_MODAL_CONFIG[decisionModal.type]
    : null;
  const isConfirmDisabled =
    !!modalConfig?.reasonRequired && !decisionModal?.reason?.trim();

  return (
    <Card
      style={{
        marginBottom: 16,
        borderColor: isBreached
          ? '#ff4d4f'
          : isActionable
            ? '#faad14'
            : '#1890ff',
        borderWidth: 2,
      }}
    >
      {/* Canary active warning banner */}
      {showCanaryActiveWarning && isActive && (
        <Alert
          type="info"
          message="Variable rollout changes are disabled during an active canary deployment. Complete or rollback the canary first."
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* Header */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={12}>
          <Badge
            status={
              isBreached ? 'error' : isActionable ? 'warning' : 'processing'
            }
            text={
              <Title level={5} style={{ margin: 0 }}>
                {isBreached
                  ? 'Canary — Threshold Breached'
                  : isActionable
                    ? 'Canary — Action Required'
                    : 'Canary Rollout In Progress'}
              </Title>
            }
          />
        </Flex>
        <Space size={8}>
          <Tag color="blue">Run #{canaryRollout.id}</Tag>
          {onRefresh && (
            <Tooltip
              title={`Auto-refreshes every 60s · Last: ${lastRefreshed.toLocaleTimeString()}`}
            >
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined {...({} as any)} />}
                onClick={() => {
                  onRefresh();
                  setLastRefreshed(new Date());
                }}
              />
            </Tooltip>
          )}
          {canaryRollout.diffId && (
            <DiffLink
              href={`/t/variables/${canaryRollout.variableName}/rollout/approve-request/${canaryRollout.diffId}`}
            >
              <LinkOutlined {...({} as any)} />
              View Changes
            </DiffLink>
          )}
        </Space>
      </Flex>

      {/* Threshold breach alert */}
      {isBreached && (
        <Alert
          type="error"
          icon={<WarningOutlined {...({} as any)} />}
          message="Metric Threshold Breached"
          description={
            <Flex vertical gap={4}>
              <span>
                Phase {(currentPhase?.phaseIndex ?? 0) + 1} has{' '}
                <strong>
                  {currentPhase?.breachCount}/{currentPhase?.totalChecks} checks
                </strong>{' '}
                breaching thresholds. Choose: Override &amp; advance, Hold to
                keep monitoring, or Rollback.
              </span>
              <BreachDetails
                phase={currentPhase!}
                metricsConfig={canaryRollout.metricsConfig}
              />
            </Flex>
          }
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* Phase awaiting decision — metric failure cause */}
      {currentPhase?.status === 'awaiting_decision' &&
        isMetricFailureCause(currentPhase) && (
          <Alert
            type="warning"
            icon={<ExclamationCircleOutlined {...({} as any)} />}
            message="Metric Read Failure — Action Required"
            description={`Phase ${(currentPhase?.phaseIndex ?? 0) + 1} (${currentPhase?.targetPercentage}%) was stopped because Datadog metric reading failed for 2 consecutive checks. Canary safety cannot be confirmed. Review the change manually before deciding to advance or rollback.`}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

      {/* Phase expired normally */}
      {currentPhase?.status === 'awaiting_decision' &&
        !isMetricFailureCause(currentPhase) && (
          <Alert
            type="info"
            icon={<ClockCircleOutlined {...({} as any)} />}
            message="Phase Duration Expired"
            description={`Phase ${(currentPhase?.phaseIndex ?? 0) + 1} (${currentPhase?.targetPercentage}%) observation period complete. Metrics healthy — ready to advance.`}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

      {/* Metadata row */}
      <Flex gap={24} style={{ marginBottom: 16 }}>
        <Flex vertical gap={2}>
          <Text type="secondary">Triggered by</Text>
          <Text strong>{canaryRollout.triggeredBy}</Text>
        </Flex>
        <Flex vertical gap={2}>
          <Text type="secondary">Elapsed</Text>
          <Text strong>{formatElapsedTime(canaryRollout.startTime)}</Text>
        </Flex>
        <Flex vertical gap={2}>
          <Text type="secondary">Current Phase</Text>
          <Text strong>
            {currentPhase
              ? `${currentPhase.name} (${currentPhase.targetPercentage}%)`
              : 'N/A'}
          </Text>
        </Flex>
        <Flex vertical gap={2}>
          <Text type="secondary">Status</Text>
          <Badge
            status={getPhaseStatusColor(currentPhase?.status ?? 'yet_to_start')}
            text={getPhaseStatusLabel(currentPhase?.status ?? 'yet_to_start')}
          />
        </Flex>
      </Flex>

      {/* Progress bar */}
      <Progress
        percent={progress}
        status={
          isBreached ? 'exception' : isActionable ? 'exception' : 'active'
        }
        style={{ marginBottom: 16 }}
      />

      {/* Decision buttons — actionable state (awaiting_decision OR threshold_breached) */}
      {isActive && isActionable && (
        <Flex gap={8} style={{ marginBottom: 16 }} wrap="wrap">
          {isBreached ? (
            /* Breach actions: Override + Hold + Rollback */
            <Tooltip title="Acknowledge the breach and advance to the next phase. This is permanently recorded in the audit trail.">
              <Button
                type="primary"
                danger
                icon={<ExclamationCircleOutlined {...({} as any)} />}
                onClick={() => openDecision('override')}
              >
                {isLastPhase ? 'Override & Promote' : 'Override & Advance'}
              </Button>
            </Tooltip>
          ) : /* Normal expiry actions: Advance or Promote */
          !isLastPhase ? (
            <Tooltip title="Move to the next canary phase with increased traffic percentage">
              <Button
                type="primary"
                icon={<FastForwardOutlined {...({} as any)} />}
                onClick={() => openDecision('advance')}
              >
                Advance to Next Phase
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Apply the canary changes to 100% of traffic permanently">
              <Button
                type="primary"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                icon={<RocketOutlined {...({} as any)} />}
                onClick={() => openDecision('promote')}
              >
                Promote (Go Live)
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Extend observation time without advancing or rolling back">
            <Button
              icon={<PauseCircleOutlined {...({} as any)} />}
              onClick={() => setShowHoldModal(true)}
            >
              Hold
            </Button>
          </Tooltip>
          <Tooltip title="Revert all changes to the pre-canary state">
            <Button
              danger
              icon={<RollbackOutlined {...({} as any)} />}
              onClick={() => openDecision('rollback')}
            >
              Rollback
            </Button>
          </Tooltip>
        </Flex>
      )}

      {/* Emergency rollback during in_progress */}
      {isActive && currentPhase?.status === 'in_progress' && (
        <Flex gap={8} style={{ marginBottom: 16 }}>
          <Tooltip title="Emergency rollback — available at any time during active monitoring">
            <Button
              size="small"
              danger
              icon={<RollbackOutlined {...({} as any)} />}
              onClick={() => openDecision('rollback')}
            >
              Emergency Rollback
            </Button>
          </Tooltip>
        </Flex>
      )}

      {/* Phase Details */}
      <PhaseListHeader>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Phase Details —{' '}
          {canaryRollout.phases.filter((p) => p.status === 'completed').length}/
          {canaryRollout.phases.length} completed
        </Text>
        <CollapseAllButton
          type="link"
          size="small"
          onClick={() => {
            if (expandedPhases.size === 0) {
              setExpandedPhases(
                new Set(canaryRollout.phases.map((p) => p.phaseIndex)),
              );
            } else {
              setExpandedPhases(new Set());
            }
          }}
        >
          {expandedPhases.size === 0 ? 'Expand all' : 'Collapse all'}
        </CollapseAllButton>
      </PhaseListHeader>

      <PhaseList>
        {canaryRollout.phases.map((phase) => {
          const isExpanded = expandedPhases.has(phase.phaseIndex);
          const monitorNames = getDatadogMonitorNames(
            canaryRollout.metricsConfig,
          );
          const monitorStatuses = getLatestMonitorStatuses(
            phase.metricCheckLog,
            canaryRollout.metricsConfig,
            phase.status,
          );
          const monitorHealth = getAggregateMonitorStatus(
            monitorStatuses.map(({ status }) => status),
          );
          const hasDetails = Boolean(
            (phase.metricCheckLog && phase.metricCheckLog.length > 0) ||
              phase.metricValuesAtStart ||
              phase.metricValuesAtEnd ||
              phase.decision ||
              phase.totalChecks > 0 ||
              monitorNames.length > 0,
          );

          return (
            <PhaseCard
              key={phase.phaseIndex}
              $status={phase.status}
              $expanded={isExpanded}
            >
              <PhaseCardHeader
                onClick={() => hasDetails && togglePhase(phase.phaseIndex)}
                $clickable={hasDetails}
              >
                <PhaseCardLeft>
                  <PhaseStatusDot $status={phase.status} />
                  <Text strong style={{ fontSize: 13 }}>
                    {phase.name} — {phase.targetPercentage}%
                  </Text>
                  <Tag color={getPhaseStatusColor(phase.status)}>
                    {getPhaseStatusLabel(phase.status)}
                  </Tag>
                  {monitorNames.length > 0 && (
                    <Tag color={getDatadogMonitorStatusColor(monitorHealth)}>
                      Monitors: {monitorHealth}
                    </Tag>
                  )}
                  {phase.thresholdBreached && phase.totalChecks > 0 && (
                    <Tag color="red">
                      {phase.breachCount}/{phase.totalChecks} checks breached
                    </Tag>
                  )}
                </PhaseCardLeft>
                <PhaseCardRight>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {phase.durationMins}min
                    {phase.totalChecks > 0 && ` · ${phase.totalChecks} checks`}
                    {phase.timeRemaining != null && phase.timeRemaining > 0 && (
                      <>
                        {' '}
                        ·{' '}
                        <span style={{ color: '#1890ff' }}>
                          {phase.timeRemaining}min left
                        </span>
                      </>
                    )}
                  </Text>
                  {phase.decision && (
                    <Tag color={getDecisionColor(phase.decision)}>
                      {DECISION_LABEL[phase.decision] ?? phase.decision}
                      {phase.decisionBy ? ` by ${phase.decisionBy}` : ''}
                    </Tag>
                  )}
                  {hasDetails && (
                    <ExpandIcon $expanded={isExpanded}>▾</ExpandIcon>
                  )}
                </PhaseCardRight>
              </PhaseCardHeader>

              {isExpanded && hasDetails && (
                <PhaseCardBody>
                  {/* Metric start/current/end comparison.
                      Show whenever there is start data OR any check log entry
                      with values — metricValuesAtEnd may be nil while the phase
                      is still running, in which case we use the latest check
                      log reading as "Current". */}
                  {(phase.metricValuesAtStart ||
                    phase.metricValuesAtEnd ||
                    lastCheckLogSnapshot(phase.metricCheckLog)) && (
                    <MetricSnapshotComparison
                      phase={phase}
                      metricsConfig={canaryRollout.metricsConfig}
                    />
                  )}
                  <CanaryMonitorStatusPanel
                    phaseStatus={phase.status}
                    entries={phase.metricCheckLog}
                    metricsConfig={canaryRollout.metricsConfig}
                  />
                  {/* Live metric log */}
                  {phase.metricCheckLog && phase.metricCheckLog.length > 0 && (
                    <>
                      <LatestMetricValues
                        log={phase.metricCheckLog}
                        metricsConfig={canaryRollout.metricsConfig}
                      />
                      <MetricChart
                        entries={phase.metricCheckLog}
                        metricsConfig={canaryRollout.metricsConfig}
                      />
                    </>
                  )}
                  {/* Decision audit */}
                  {phase.decision && (
                    <Flex vertical gap={2}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Decision:{' '}
                        <strong>
                          {DECISION_LABEL[phase.decision] ?? phase.decision}
                        </strong>
                        {phase.decisionBy && ` by ${phase.decisionBy}`}
                        {phase.decisionAt && (
                          <> · {new Date(phase.decisionAt).toLocaleString()}</>
                        )}
                      </Text>
                      {phase.decisionReason && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Reason: {phase.decisionReason}
                        </Text>
                      )}
                    </Flex>
                  )}
                  {/* Timestamps */}
                  {phase.startTime && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Started: {new Date(phase.startTime).toLocaleString()}
                      {phase.endTime &&
                        ` · Ended: ${new Date(phase.endTime).toLocaleString()}`}
                    </Text>
                  )}
                  {/* Last metric check */}
                  {phase.metricCheckLog && phase.metricCheckLog.length > 0 && (
                    <LastCheckedText>
                      Last checked:{' '}
                      {new Date(
                        phase.metricCheckLog[
                          phase.metricCheckLog.length - 1
                        ].checkedAt,
                      ).toLocaleTimeString()}
                    </LastCheckedText>
                  )}
                </PhaseCardBody>
              )}
            </PhaseCard>
          );
        })}
      </PhaseList>

      {/* Hold Modal */}
      <Modal
        title="Hold — Extend Observation"
        open={showHoldModal}
        onOk={() => {
          onHold?.(canaryRollout.id, holdMins ?? 30);
          setShowHoldModal(false);
        }}
        okButtonProps={{ disabled: !holdMins }}
        onCancel={() => setShowHoldModal(false)}
        okText="Hold"
      >
        <Flex vertical gap={12}>
          <Text>
            Keep the current phase running for additional observation time.
          </Text>
          <Flex align="center" gap={8}>
            <Text>Additional minutes:</Text>
            <InputNumber
              value={holdMins}
              min={1}
              max={1440}
              onChange={(v) => setHoldMins(v)}
            />
          </Flex>
        </Flex>
      </Modal>

      {/* Decision Reason Modal */}
      {decisionModal && modalConfig && (
        <Modal
          title={modalConfig.title}
          open={!!decisionModal}
          onOk={handleDecisionConfirm}
          onCancel={() => setDecisionModal(null)}
          okText={modalConfig.okText}
          okButtonProps={{
            danger: modalConfig.okDanger,
            disabled: isConfirmDisabled,
          }}
        >
          <Flex vertical gap={12}>
            {/* Metric failure warning — shown when the phase stopped due to read failures */}
            {isMetricFailureCause(currentPhase) &&
              (decisionModal.type === 'advance' ||
                decisionModal.type === 'promote') && (
                <Alert
                  type="warning"
                  showIcon
                  message="Metric data was unavailable"
                  description="This phase stopped because Datadog metric reading failed for 2 consecutive checks. You are advancing without confirmed metric health. This decision will be recorded in the audit trail."
                />
              )}
            {modalConfig.description && (
              <Alert
                type={modalConfig.okDanger ? 'warning' : 'info'}
                message={modalConfig.description}
                showIcon
              />
            )}
            <div>
              <Text>
                Reason
                {modalConfig.reasonRequired ? ' (required)' : ' (optional)'}:
              </Text>
              <Input.TextArea
                value={decisionModal.reason}
                onChange={(e) =>
                  setDecisionModal((d) =>
                    d ? { ...d, reason: e.target.value } : d,
                  )
                }
                rows={3}
                placeholder={
                  modalConfig.reasonRequired
                    ? 'Please provide a reason for this action…'
                    : 'Optional: add a note for the audit trail'
                }
                style={{ marginTop: 6 }}
              />
            </div>
          </Flex>
        </Modal>
      )}
    </Card>
  );
};

function getDecisionColor(decision: string): string {
  switch (decision) {
    case 'user_promoted':
    case 'promoted':
      return 'green';
    case 'user_rolled_back':
    case 'rolled_back':
      return 'red';
    case 'advance':
      return 'blue';
    case 'user_overridden':
      return 'orange';
    case 'user_held':
      return 'gold';
    default:
      return 'default';
  }
}

// ---------------------------------------------------------------------------
// Phase list styled components
// ---------------------------------------------------------------------------

const PhaseListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const CollapseAllButton = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 12px;
`;

const PhaseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const STATUS_BORDER: Record<string, string> = {
  in_progress: '#1890ff',
  awaiting_decision: '#faad14',
  threshold_breached: '#ff4d4f',
  completed: '#52c41a',
  rolled_back: '#ff4d4f',
  yet_to_start: '#d9d9d9',
  skipped: '#722ed1',
};

const STATUS_BG: Record<string, string> = {
  in_progress: '#f0f7ff',
  awaiting_decision: '#fffbe6',
  threshold_breached: '#fff2f0',
  completed: '#f6ffed',
  rolled_back: '#fff2f0',
  yet_to_start: '#fafafa',
  skipped: '#f9f0ff',
};

const PhaseCard = styled.div<{ $status: string; $expanded: boolean }>`
  border: 1px solid ${({ $status }) => STATUS_BORDER[$status] ?? '#e8e8e8'};
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  ${({ $expanded, $status }) =>
    $expanded && `background: ${STATUS_BG[$status] ?? '#fafafa'};`}
`;

const PhaseCardHeader = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  user-select: none;
  &:hover {
    background: ${({ $clickable }) =>
      $clickable ? 'rgba(0,0,0,0.02)' : 'transparent'};
  }
`;

const PhaseCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PhaseCardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const DOT_COLOR: Record<string, string> = {
  in_progress: '#1890ff',
  awaiting_decision: '#faad14',
  threshold_breached: '#ff4d4f',
  completed: '#52c41a',
  rolled_back: '#ff4d4f',
  yet_to_start: '#d9d9d9',
  skipped: '#722ed1',
};

const PhaseStatusDot = styled.span<{ $status: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $status }) => DOT_COLOR[$status] ?? '#d9d9d9'};
  flex-shrink: 0;
  ${({ $status }) =>
    ($status === 'in_progress' || $status === 'threshold_breached') &&
    `
    box-shadow: 0 0 0 3px ${
      $status === 'threshold_breached'
        ? 'rgba(255,77,79,0.2)'
        : 'rgba(24,144,255,0.2)'
    };
    animation: pulse 1.5s ease-in-out infinite;
  `}
  @keyframes pulse {
    0%,
    100% {
      box-shadow: 0 0 0 3px
        ${({ $status }) =>
          $status === 'threshold_breached'
            ? 'rgba(255,77,79,0.2)'
            : 'rgba(24,144,255,0.2)'};
    }
    50% {
      box-shadow: 0 0 0 6px
        ${({ $status }) =>
          $status === 'threshold_breached'
            ? 'rgba(255,77,79,0.05)'
            : 'rgba(24,144,255,0.05)'};
    }
  }
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.35);
  transition: transform 0.2s;
  transform: ${({ $expanded }) =>
    $expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  display: inline-block;
`;

const PhaseCardBody = styled.div`
  padding: 10px 14px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export default CanaryStatusSection;
